import moment from "moment";
import {Card, Chip, Stack} from "@mui/material";
import React, {useEffect, useState} from "react";
import { formatDate, formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import {
  Datagrid,
  List,
  ShowButton,
  TextField,
  useUnselectAll,
  useListController,
  useRecordContext,
  useRefresh,
  FunctionField, TopToolbar, CreateButton,
  ArrayField,
  SimpleShowLayout
} from "react-admin";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { VendorPayoutTypesEnum } from "../../enums/VendorPayoutTypesEnum";
import { VendorPayoutTransactionStatus } from "../../enums/vendorPayoutTransactionStatus";
import EventBus from "../../EventBus";
import { VendorPayoutResponse } from "../../interfaces/VendorPayout/VendorPayoutResponse";
import VendorPayoutService from "../../services/VendorPayoutService";
import CustomReferenceField from "../Common/CustomReferenceField";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { PayoutTypeDailog } from "../Common/payoutTypeDialog";
import RejectPayoutReasonDialog from "../Common/RejectPayoutReasonDialog";
import TransactionButton from "../Common/TransactionButton";
import MetabaseDashboardButton from '../Common/MetabaseDashboardButton';
import CommonFilterCard, { CommonFilterField } from "../Common/CommonFilterCard";
import StyledDatagridWrapper, { commonDatagridSx } from "../Common/StyledDatagridWrapper";

const vendorPayoutService = new VendorPayoutService();

interface ActionButtonsInputProps {
  setPayoutTypeDailog: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedData: React.Dispatch<
    React.SetStateAction<VendorPayoutResponse | null>
  >;
  recordType?: string;
  isBulkAction: boolean;
  setBulkAction: React.Dispatch<React.SetStateAction<boolean>>;
}

const ProcessPayout = (props: ActionButtonsInputProps) => {
  const record = useRecordContext();

  const handleClick = () => {
    props?.setBulkAction(props?.isBulkAction);
    props.setPayoutTypeDailog(true);
    props.setSelectedData(record as VendorPayoutResponse);
  };

  return (
    <TransactionButton onClickHandler={handleClick} name={"Process Payout"} />
  );
};

const ApprovePayout: React.FC<{ isBulkAction: boolean }> = ({
  isBulkAction,
}) => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const { selectedIds } = useListController();
  const unselectAll = useUnselectAll("VendorPayouts");
  const bulkApproveHandler = async() => {
    if(selectedIds){
      EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
        message: "Are you sure you want to approve these payouts?",
        onClose: async (confirmed: boolean) => {
            if (confirmed) {
              const payload = {
                transactionId: selectedIds,
                comments: '',
              }
              await vendorPayoutService.bulkApprovePayout(payload).then((res) => {
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                    message: "Success",
                    type: CustomSnackbarTypes.SUCCESS,
                });
                refresh();
            }).catch((err) => {
                console.error(err);
            });
            }
        },
    });
 } 
    unselectAll();
  }
  // const bulkApproveHandler = () => {
  //   selectedIds.forEach(async (vendorPayoutID) => {
  //     try {
  //       const response = await vendorPayoutService.approvePayout(
  //         Number(vendorPayoutID)
  //       );
  //       EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
  //         message: response.status,
  //         type: CustomSnackbarTypes.SUCCESS,
  //       });
  //       refresh();
  //     } catch (error) {
  //       console.error(error);
  //     }
  //   });
  //   unselectAll();
  // };

  const handleClick = async () => {
    EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
      message: "Are you sure you want to approve this payout?",
      onClose: async (confirmed: boolean) => {
          if (confirmed) {
            await vendorPayoutService.approvePayout(
              Number(record?.id)
            ).then((res) => {
              EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                  message: "Success",
                  type: CustomSnackbarTypes.SUCCESS,
              });
              refresh();
          }).catch((err) => {
              console.error(err);
          });
          }
      },
  });
  };

  return (
    <TransactionButton
      onClickHandler={isBulkAction ? bulkApproveHandler : handleClick}
      color="success"
      name={"Approve Payout"}
    />
  );
};

const RejectPayout: React.FC<{ isBulkAction: boolean }> = ({
  isBulkAction,
}) => {
  const [open, setOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [dialogShowAllReasons, setDialogShowAllReasons] = useState(false);
  const record = useRecordContext();
  const { selectedIds, data } = useListController();
  const normalizeType = (type?: string) => (type || "").toUpperCase();
  const resolvePayoutType = (row: any) => {
    if (
      normalizeType(row?.vendorPayoutEvent?.event) ===
      VendorPayoutTypesEnum.CONVEYANCE
    ) {
      return VendorPayoutTypesEnum.CONVEYANCE;
    }
    return normalizeType(
      row?.vendorPayoutEvent?.type ||
        row?.type ||
        row?.referenceModel?.payoutEvent,
    );
  };

  const selectedPayouts = (data || []).filter((row: any) =>
    selectedIds?.includes(row?.id),
  );
  const validateReasonSelection = (key: string) => {
    return [
      VendorPayoutTypesEnum.INCENTIVE,
      VendorPayoutTypesEnum.PENALTY,
      VendorPayoutTypesEnum.CONVEYANCE,
    ].includes(key as VendorPayoutTypesEnum);
  };
  const openRejectDialog = () => {
    const resolvedType = isBulkAction
      ? resolvePayoutType(selectedPayouts?.[0])
      : resolvePayoutType(record);
    const resolvedShowAllReasons = isBulkAction
      ? selectedPayouts.length > 0 &&
        selectedPayouts.every((row: any) =>
          validateReasonSelection(resolvePayoutType(row)),
        )
      : validateReasonSelection(resolvedType);

    setDialogType(resolvedType);
    setDialogShowAllReasons(resolvedShowAllReasons);
    setOpen(true);
  };

  return (
    <>
      <RejectPayoutReasonDialog
        open={open}
        setOpen={setOpen}
        id={isBulkAction ? undefined : Number(record?.id)}
        isBulkAction={isBulkAction}
        selectedVendorPayoutIds={selectedIds}
        showAllReasons={dialogShowAllReasons}
        type={dialogType}
      />
      <TransactionButton
        onClickHandler={openRejectDialog}
        color="error"
        name={"Reject Payout"}
      />
    </>
  );
};

const RendorCTAButtons: React.FC<ActionButtonsInputProps> = ({
  setPayoutTypeDailog,
  setSelectedData,
  recordType,
  isBulkAction = false,
  setBulkAction,
}) => {
  const record = useRecordContext();
  return (
    <Stack direction={"row"} gap={2}>
      {[VendorPayoutTransactionStatus.PENDING, VendorPayoutTransactionStatus.APPROVED].includes(
        record?.status ?? recordType
      ) && (
        <>
          <ApprovePayout isBulkAction={isBulkAction} />
          <RejectPayout isBulkAction={isBulkAction} />
        </>
      )}
      {[
        VendorPayoutTransactionStatus.APPROVED,
        VendorPayoutTransactionStatus.PROCESSING_FAILED,
      ].includes(record?.status ?? recordType) && (
        <ProcessPayout
          setPayoutTypeDailog={setPayoutTypeDailog}
          setSelectedData={setSelectedData}
          isBulkAction={isBulkAction}
          setBulkAction={setBulkAction}
        />
      )}
    </Stack>
  );
};

const ListActions = (props: any) => {
  return (
      <TopToolbar>
        <CreateButton />
      </TopToolbar>
  )
};

export const VendorPayoutList = (props: any) => {

  const [eventList, setEventList] = useState<any>([]);
  const [productList, setProductList] = useState<any>([]);
  const [typeList, setTypeList] = useState<any>([]);
  const [payoutTypeDailog, setPayoutTypeDailog] = useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<VendorPayoutResponse | null>(
    null
  );

  const filterFields: CommonFilterField[] = [
    { type: "text", label: "Cook Id", source: "cookId", numeric: true },
    {
      type: "select",
      label: "Payout status",
      source: "status",
      options: Object.values(VendorPayoutTransactionStatus).map((key) => ({
        value: key,
        label: key,
      })),
    },
    {
      type: "select",
      label: "Payout Type",
      source: "type",
      options: (typeList ?? []).map((key: string) => ({
        value: key,
        label: key,
      })),
    },
    {
      type: "select",
      label: "Payout Event",
      source: "event",
      options: (eventList ?? []).map((key: string) => ({
        value: key,
        label: key,
      })),
    },
    {
      type: "select",
      label: "Payout Product",
      source: "product",
      options: (productList ?? []).map((key: string) => ({
        value: key,
        label: key,
      })),
    },
    { type: "date", label: "From Created At Date", source: "startDates" },
    { type: "date", label: "To Created At Date", source: "endDates" },
  ];

  const [bulkAction, setBulkAction] = useState<boolean>(false);
  const { selectedIds, data } = useListController();
  function checkActionType() {
    let selectedPayouts = data?.filter((row) => selectedIds.includes(row.id));
    let statusType = selectedPayouts?.[0]?.status ?? "";
    if (statusType === "") return <></>;
    if (
      selectedPayouts?.filter((row) => row.status === statusType)?.length ===
      selectedPayouts?.length
    ) {
      return (
        <RendorCTAButtons
          setPayoutTypeDailog={setPayoutTypeDailog}
          setSelectedData={setSelectedData}
          recordType={statusType}
          isBulkAction={true}
          setBulkAction={setBulkAction}
        />
      );
    }
    return <></>;
  }

  useEffect(() => {
    vendorPayoutService.getPayoutEvents().then(async (res) => {
      const typesSet = new Set();
      const eventsSet = new Set();
      const productsSet = new Set();
      await res?.forEach((each: any) => {
        typesSet.add(each?.type);
        eventsSet.add(each?.event);
        productsSet.add(each?.product);
      });
      setEventList(Array.from(eventsSet));
      setProductList(Array.from(productsSet));
      setTypeList(Array.from(typesSet));
    })
  }, [])
  return (
    <>
      <PayoutTypeDailog
        selectedVendorPayoutIds={selectedIds}
        data={selectedData}
        open={payoutTypeDailog}
        isBulkAction={bulkAction}
        callback={() => {
          setPayoutTypeDailog(false);
        }}
      />
      <List
          actions={<ListActions {...props} />}
          sx={{
            "& .RaList-content": {
              boxShadow: "none",
              backgroundColor: "transparent",
            },
          }}
      >
        <>
        <CommonFilterCard fields={filterFields} />

        <br />

        <StyledDatagridWrapper title="Vendor Payouts">
        <Datagrid
          rowClick="edit"
          sx={{
            ...commonDatagridSx,
            ...(selectedIds?.length !== 0
              ? { mt: "3rem" }
              : { "& .RaBulkActionsToolbar-toolbar": { display: "none" } }),
          }}
          bulkActionButtons={checkActionType()}
        >
          <TextField source="id" />
          <CustomReferenceField listName={"Cooks"} source={"cookId"} />
          <FunctionField label="Cook Name" render={(record: any) => (
            <div>{`${record?.cook?.cookFullProfile?.firstName} ${record?.cook?.cookFullProfile?.lastName}`}</div>
          )} />
          <FunctionField label="Cook Penalty" render={(record: any) => {
            const startDate = moment().startOf('month').format('YYYY-MM-DD');
            const endDate = moment().format('YYYY-MM-DD');
            return (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <MetabaseDashboardButton
                  label="Dashboard"
                  href={`https://metabase.thechefkart.com/question/6675-dod-cook-penalty?startDate=${startDate}&endDate=${endDate}&cookId=${record?.cookId ?? record?.cook?.id ?? ""}&Mgtype=`}
                />
              </div>
            );
          }} />
          {/* <TextField label="Cook Name" source="cook.cookFullProfile.firstName" sortable={false} /> */}
          {/*<TextField source="type" sortable={false} />*/}
          <TextField source="amount" />
          <TextField label="Description" source="comments" />
          <FunctionField
              label="Event"
              render={(record: any) => (
                  <div>{`${record?.vendorPayoutEvent?.event || ''}`}</div>
              )}
          />
          <FunctionField
              label="Product"
              render={(record: any) => (
                  <div>{`${record?.vendorPayoutEvent?.product || ''}`}</div>
              )}
          />
          <FunctionField
              label="Type"
              render={(record: any) => (
                  <div>{`${record?.vendorPayoutEvent?.type || ''}`}</div>
              )}
          />
          <FunctionField
            label="Status"
            render={(record: any) => {
              const isPendingResubmitted =
                record?.status === VendorPayoutTransactionStatus.PENDING &&
                !!record?.resubmittedAt;
              if (isPendingResubmitted) {
                return (
                  <Chip
                    size="small"
                    label="PENDING (RESUBMITTED)"
                    color="warning"
                    variant="filled"
                  />
                );
              }
              return <span>{record?.status ?? ""}</span>;
            }}
          />
          <FunctionField label="last Update" render={(record: any) => (
            <span style={{ whiteSpace: 'nowrap', display: 'inline-block', minWidth: '100px' }}>
              {record?.updatedAt ? formatDate(record.updatedAt) : '-'}
            </span>
          )} />
          <RendorCTAButtons
            setPayoutTypeDailog={setPayoutTypeDailog}
            setSelectedData={setSelectedData}
            isBulkAction={false}
            setBulkAction={setBulkAction}
          />
          <Card
              style={{
                overflow: "auto",
                borderRadius: "4px",
                height: "12rem",
                boxShadow: "rgba(0, 0, 0, 0.35) 0px 1px 3px",
                width: "30vw",
              }}
              elevation={0}
            >

              <SimpleShowLayout>
                <ArrayField
                  emptyText="Not Available"
                  source="payoutComments"
                >
                  <Datagrid bulkActionButtons={false}>
                    <TextField emptyText="Not Available" source="comment" />
                    <TextField
                      emptyText="Not Available"
                      source="commenterEmail"
                    />
                    <FunctionField
                      label="Timestamp"
                      render={(record: any) =>
                        record?.timestamp ? (
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {formatDateTimeWithSeconds(record.timestamp)}
                          </span>
                        ) : 'Not Available'
                      }
                    />
                  </Datagrid>
                </ArrayField>
              </SimpleShowLayout>
            </Card>
          <ShowButton />
        </Datagrid>
        </StyledDatagridWrapper>
        </>
      </List>
    </>
  );
};
